import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Account, AccountType, AuthType, getPlatformIdFromPlatform, getVirtualEnvironment, notEmpty, Platform, validateAndFormatAddress, VMType, } from "@orb-labs/types";
import { getTypesForEIP712Domain, serializeTypedData, serializeTransaction } from "viem";
import { usePlatformStorage } from "../../storage/PlatformStorageContext.js";
import { useMesh } from "../mesh/MeshProvider.js";
import { useMnemonic } from "../mnemonic/MnemonicProvider.js";
import { capture } from "../../observability/capture.js";
import { AuthEvents } from "../../bullrun/analytics.js";
const AuthenticationContext = createContext(undefined);
export const useAuthentication = () => {
    const context = useContext(AuthenticationContext);
    if (!notEmpty(context)) {
        throw new Error("useAuthentication must be used within an AuthenticationProvider");
    }
    return context;
};
export const AuthenticationProvider = ({ authRuntime, children }) => {
    const { clientState, session, user, wallets, turnkeySignTransaction, turnkeySignMessage, turnkeyLogout, turnkeyInitOtp, turnkeyCompleteOtp, turnkeyHandleGoogleOauth, turnkeyHandleAppleOauth, additionalLogoutSteps, checkTransactionSecurity, } = authRuntime;
    const storage = usePlatformStorage();
    const { linkedAccounts: meshLinkedAccounts, reset: resetMesh } = useMesh();
    const { accounts: mnemonicAccounts, isLoading: mnemonicLoading, reset: resetMnemonic, signTransaction: mnemonicSignTransaction, signTypedData: mnemonicSignTypedData, isOwnAddress: isMnemonicAddress, importWallet: mnemonicImportWallet, } = useMnemonic();
    const [authentication, setAuthentication] = useState(undefined);
    // Track the last authenticated Turnkey user to detect user switches.
    // `lastAuthUserIdLoaded` gates on the initial storage read; `lastAuthUserId`
    // is the string userId or undefined when no prior Turnkey user exists.
    const [lastAuthUserIdLoaded, setLastAuthUserIdLoaded] = useState(false);
    const [lastAuthUserId, setLastAuthUserIdState] = useState(undefined);
    const lastProcessedSessionRef = useRef(undefined);
    useEffect(() => {
        storage
            .getLastAuthUserId()
            .then((stored) => {
            setLastAuthUserIdState(stored ?? undefined);
        })
            .catch((error) => {
            // If storage read fails we still need to unblock the loading gate —
            // otherwise isLoading stays true forever and the app never renders.
            console.error("[AuthenticationProvider] Failed to read lastAuthUserId from storage:", error);
            setLastAuthUserIdState(undefined);
        })
            .finally(() => {
            setLastAuthUserIdLoaded(true);
        });
    }, [storage]);
    // When a new Turnkey session is established, check if a different user is logging in
    // and clear stale mnemonic + mesh data from the previous user.
    useEffect(() => {
        if (clientState !== "ready" || !session?.userId)
            return;
        if (lastProcessedSessionRef.current === session.userId)
            return;
        const processingUserId = session.userId;
        lastProcessedSessionRef.current = processingUserId;
        const handleSessionEstablished = async () => {
            const storedUserId = await storage.getLastAuthUserId();
            if (storedUserId && storedUserId !== processingUserId) {
                console.info("[AuthenticationProvider] User switch detected, clearing previous user data");
                await resetMnemonic();
                await resetMesh();
            }
            await storage.setLastAuthUserId(processingUserId);
            setLastAuthUserIdState(processingUserId);
        };
        handleSessionEstablished().catch((error) => {
            console.error("[AuthenticationProvider] Failed to establish session:", error);
            // Clear the dedup ref so a re-render can retry — otherwise a transient
            // storage failure would permanently prevent the user-switch guard from
            // running for this session.
            if (lastProcessedSessionRef.current === processingUserId) {
                lastProcessedSessionRef.current = undefined;
            }
        });
    }, [clientState, session?.userId, resetMnemonic, resetMesh, storage]);
    // Reset the processed session ref when session is lost so the user-switch check
    // runs again on next login.
    useEffect(() => {
        if (!session) {
            lastProcessedSessionRef.current = undefined;
        }
    }, [session]);
    const isLoading = mnemonicLoading || !lastAuthUserIdLoaded;
    // Require a Turnkey session for users who previously authenticated via Turnkey.
    // On web, mnemonic accounts are always empty, so this reduces to `!!session`.
    const isAuthenticated = useMemo(() => {
        if (session)
            return true;
        if (mnemonicAccounts.length > 0) {
            if (!lastAuthUserIdLoaded)
                return false;
            // No prior Turnkey user — mnemonic-only user is authenticated.
            // Had a prior Turnkey user — require Turnkey session.
            return !notEmpty(lastAuthUserId);
        }
        return false;
    }, [session, lastAuthUserIdLoaded, lastAuthUserId, mnemonicAccounts]);
    // ---------------------------------------------------------------------------
    // Accounts
    // ---------------------------------------------------------------------------
    const accounts = useMemo(() => {
        if (clientState !== "ready")
            return [];
        const turnkeyAccounts = wallets
            .filter((wallet) => wallet.accounts && wallet.accounts.length > 0)
            .map((wallet) => wallet.accounts.map((account) => {
            if (account.addressFormat === "ADDRESS_FORMAT_ETHEREUM") {
                return new Account(account.address, AccountType.EOA, VMType.EVM, undefined);
            }
            else if (account.addressFormat === "ADDRESS_FORMAT_SOLANA") {
                return new Account(account.address, AccountType.EOA, VMType.SVM, undefined);
            }
            return undefined;
        }))
            .flat()
            .filter(notEmpty);
        if (session?.userId) {
            turnkeyAccounts.push(new Account(session.userId, AccountType.SMART_CONTRACT, VMType.OFFCHAIN, BigInt(getPlatformIdFromPlatform(Platform.TURNKEY))));
        }
        const cexAccounts = meshLinkedAccounts.map((linkedAccount) => new Account(linkedAccount.address, AccountType.SMART_CONTRACT, VMType.OFFCHAIN, BigInt(getPlatformIdFromPlatform(Platform.MESH_CONNECT))));
        return mnemonicAccounts.concat(turnkeyAccounts).concat(cexAccounts);
    }, [wallets, mnemonicAccounts, session?.userId, clientState, meshLinkedAccounts]);
    // ---------------------------------------------------------------------------
    // Authentication state
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (session?.userId) {
            if (user?.userEmail) {
                setAuthentication({
                    method: AuthType.EMAIL,
                    value: user.userEmail,
                    externalUserId: session.userId,
                });
            }
            else if (user?.userPhoneNumber) {
                setAuthentication({
                    method: AuthType.PHONE,
                    value: user.userPhoneNumber,
                    externalUserId: session.userId,
                });
            }
            else if (clientState === "ready") {
                // Wallet-connect users: session exists but no email/phone.
                const firstAddress = wallets.flatMap((w) => w.accounts ?? []).find(notEmpty)?.address;
                const formatted = notEmpty(firstAddress) ? validateAndFormatAddress(firstAddress) : undefined;
                if (notEmpty(formatted)) {
                    setAuthentication({
                        method: AuthType.WALLET,
                        value: formatted,
                        externalUserId: session.userId,
                    });
                }
            }
        }
        else if (mnemonicAccounts.length > 0) {
            const walletAddress = validateAndFormatAddress(mnemonicAccounts[0]?.address);
            if (notEmpty(walletAddress)) {
                setAuthentication({
                    method: AuthType.WALLET,
                    value: walletAddress,
                    externalUserId: walletAddress,
                });
            }
            else {
                setAuthentication(undefined);
            }
        }
        else {
            setAuthentication(undefined);
        }
    }, [session?.userId, user?.userEmail, user?.userPhoneNumber, wallets, clientState, mnemonicAccounts]);
    // ---------------------------------------------------------------------------
    // Wallet lookup
    // ---------------------------------------------------------------------------
    const findTurnkeyWalletAccount = useCallback((address) => {
        const normalized = validateAndFormatAddress(address);
        for (const wallet of wallets) {
            for (const account of wallet.accounts ?? []) {
                if (validateAndFormatAddress(account.address) === normalized) {
                    return account;
                }
            }
        }
        return undefined;
    }, [wallets]);
    // ---------------------------------------------------------------------------
    // Turnkey transaction signing — handles EVM/SVM via transactionType param
    // ---------------------------------------------------------------------------
    const signWithTurnkey = useCallback(async (operation, turnkeyAccount) => {
        const vmType = getVirtualEnvironment(operation.platformId);
        if (vmType === VMType.SVM) {
            const unsignedTx = operation.data.startsWith("0x") ? operation.data.slice(2) : operation.data;
            return turnkeySignTransaction({
                unsignedTransaction: unsignedTx,
                transactionType: "TRANSACTION_TYPE_SOLANA",
                walletAccount: turnkeyAccount,
            });
        }
        const chainId = Number(operation.platformId);
        // Validate required EVM signing fields up front — Number(undefined) is
        // NaN and would silently produce an invalid signature. Fail loud instead.
        if (!notEmpty(operation.nonce)) {
            throw new Error("Cannot sign EVM transaction: missing nonce");
        }
        if (!notEmpty(operation.gasLimit)) {
            throw new Error("Cannot sign EVM transaction: missing gasLimit");
        }
        const isEip1559 = notEmpty(operation.maxFeePerGas);
        if (isEip1559) {
            if (!notEmpty(operation.maxPriorityFeePerGas)) {
                throw new Error("Cannot sign EIP-1559 transaction: missing maxPriorityFeePerGas");
            }
        }
        else if (!notEmpty(operation.gasPrice)) {
            throw new Error("Cannot sign legacy EVM transaction: missing gasPrice");
        }
        const serialized = serializeTransaction(isEip1559
            ? {
                type: "eip1559",
                to: operation.to,
                data: operation.data,
                value: operation.value ?? BigInt(0),
                nonce: Number(operation.nonce),
                gas: operation.gasLimit,
                maxFeePerGas: operation.maxFeePerGas,
                maxPriorityFeePerGas: operation.maxPriorityFeePerGas,
                chainId,
            }
            : {
                type: "legacy",
                to: operation.to,
                data: operation.data,
                value: operation.value ?? BigInt(0),
                nonce: Number(operation.nonce),
                gas: operation.gasLimit,
                gasPrice: operation.gasPrice,
                chainId,
            });
        const unsignedTx = serialized.startsWith("0x") ? serialized.slice(2) : serialized;
        const signedTx = await turnkeySignTransaction({
            unsignedTransaction: unsignedTx,
            transactionType: "TRANSACTION_TYPE_ETHEREUM",
            walletAccount: turnkeyAccount,
        });
        return signedTx.startsWith("0x") ? signedTx : `0x${signedTx}`;
    }, [turnkeySignTransaction]);
    // ---------------------------------------------------------------------------
    // signTransaction — route to Turnkey or Mnemonic
    // ---------------------------------------------------------------------------
    const signTransaction = useCallback(async (operation) => {
        if (checkTransactionSecurity)
            checkTransactionSecurity();
        const { from } = operation;
        if (!from)
            throw new Error("Operation missing 'from' address");
        const turnkeyAccount = findTurnkeyWalletAccount(from);
        if (turnkeyAccount) {
            return signWithTurnkey(operation, turnkeyAccount);
        }
        else if (isMnemonicAddress(from)) {
            return mnemonicSignTransaction(operation);
        }
        throw new Error(`No wallet found for address: ${from.slice(0, 6)}...${from.slice(-4)}`);
    }, [checkTransactionSecurity, findTurnkeyWalletAccount, signWithTurnkey, isMnemonicAddress, mnemonicSignTransaction]);
    // ---------------------------------------------------------------------------
    // signTypedData — sign EIP-712 typed data (EVM-only)
    // ---------------------------------------------------------------------------
    const signTypedData = useCallback(async (operation) => {
        if (checkTransactionSecurity)
            checkTransactionSecurity();
        const { from } = operation;
        if (!from)
            throw new Error("Operation missing 'from' address");
        const turnkeyAccount = findTurnkeyWalletAccount(from);
        if (turnkeyAccount) {
            const typedData = JSON.parse(operation.data);
            const typesWithDomain = {
                EIP712Domain: getTypesForEIP712Domain({ domain: typedData.domain }),
                ...typedData.types,
            };
            const serializedPayload = serializeTypedData({
                domain: typedData.domain,
                types: typesWithDomain,
                primaryType: typedData.primaryType,
                message: typedData.message,
            });
            const result = await turnkeySignMessage({
                message: serializedPayload,
                walletAccount: turnkeyAccount,
                encoding: "PAYLOAD_ENCODING_EIP712",
                hashFunction: "HASH_FUNCTION_NO_OP",
                addEthereumPrefix: false,
            });
            const r = (result.r ?? "").padStart(64, "0");
            const s = (result.s ?? "").padStart(64, "0");
            const vInt = parseInt(result.v ?? "0", 16);
            const v = (vInt < 27 ? vInt + 27 : vInt).toString(16).padStart(2, "0");
            return `0x${r}${s}${v}`;
        }
        else if (isMnemonicAddress(from)) {
            return mnemonicSignTypedData(operation);
        }
        throw new Error(`No wallet found for address: ${from.slice(0, 6)}...${from.slice(-4)}`);
    }, [checkTransactionSecurity, findTurnkeyWalletAccount, turnkeySignMessage, isMnemonicAddress, mnemonicSignTypedData]);
    // ---------------------------------------------------------------------------
    // signUserOperation — stub; ERC-4337 bundler integration not yet implemented
    // ---------------------------------------------------------------------------
    const signUserOperation = useCallback(async (_operations, _accountAddress, _platformId, _txRpcUrl) => {
        if (checkTransactionSecurity)
            checkTransactionSecurity();
        throw new Error("UserOperation signing is not yet supported. " +
            "Smart contract account transactions should use sponsored intents.");
    }, [checkTransactionSecurity]);
    // ---------------------------------------------------------------------------
    // Logout
    // ---------------------------------------------------------------------------
    // Logout is intentionally non-throwing. The user pressed "log out" — their
    // local state must always be cleared, even if remote session invalidation
    // (turnkeyLogout), storage cleanup, or app-specific steps fail partway
    // through. Each step runs independently so a failure in one does not skip
    // the others; all errors are logged but swallowed so fire-and-forget call
    // sites (e.g. web header dropdown) don't produce unhandled rejections.
    const logout = useCallback(async () => {
        capture(AuthEvents.LOGOUT, {});
        const runStep = async (label, step) => {
            try {
                await step();
            }
            catch (error) {
                console.error(`[AuthenticationProvider] Logout step "${label}" failed:`, error);
            }
        };
        await runStep("turnkeyLogout", turnkeyLogout);
        await runStep("resetMnemonic", resetMnemonic);
        await runStep("resetMesh", resetMesh);
        await runStep("clearLastAuthUserId", () => storage.clearLastAuthUserId());
        if (additionalLogoutSteps) {
            await runStep("additionalLogoutSteps", additionalLogoutSteps);
        }
        setLastAuthUserIdState(undefined);
        setAuthentication(undefined);
    }, [turnkeyLogout, resetMnemonic, resetMesh, storage, additionalLogoutSteps]);
    // ---------------------------------------------------------------------------
    // Login actions — Turnkey primitives are injected via AuthRuntime so this
    // provider never imports @turnkey/* directly. Each app's InfrastructureBridge
    // wires its platform SDK (react-native-wallet-kit vs react-wallet-kit).
    // ---------------------------------------------------------------------------
    const errorMessage = (error, fallback) => error instanceof Error ? error.message : fallback;
    const sendOtpCode = useCallback(async (email) => {
        try {
            const otpId = await turnkeyInitOtp({ contact: email, otpType: "EMAIL" });
            if (!otpId)
                throw new Error("Failed to initialize OTP");
            return otpId;
        }
        catch (error) {
            console.error("[AuthenticationProvider] sendOtpCode failed:", error);
            throw new Error(errorMessage(error, "Failed to send verification code"));
        }
    }, [turnkeyInitOtp]);
    const completeEmailOtp = useCallback(async (otpId, otpCode, email) => {
        try {
            const result = await turnkeyCompleteOtp({ otpId, otpCode, contact: email, otpType: "EMAIL" });
            return { isNewUser: result?.action === "SIGNUP" };
        }
        catch (error) {
            console.error("[AuthenticationProvider] completeEmailOtp failed:", error);
            throw error;
        }
    }, [turnkeyCompleteOtp]);
    const sendSmsOtpCode = useCallback(async (phoneNumber) => {
        try {
            const otpId = await turnkeyInitOtp({ contact: phoneNumber, otpType: "SMS" });
            if (!otpId)
                throw new Error("Failed to initialize SMS OTP - no otpId returned");
            return otpId;
        }
        catch (error) {
            console.error("[AuthenticationProvider] sendSmsOtpCode failed:", error);
            throw new Error(errorMessage(error, "Failed to send verification code"));
        }
    }, [turnkeyInitOtp]);
    const completeSmsOtp = useCallback(async (otpId, otpCode, phoneNumber) => {
        try {
            const result = await turnkeyCompleteOtp({ otpId, otpCode, contact: phoneNumber, otpType: "SMS" });
            return { isNewUser: result?.action === "SIGNUP" };
        }
        catch (error) {
            console.error("[AuthenticationProvider] completeSmsOtp failed:", error);
            throw error;
        }
    }, [turnkeyCompleteOtp]);
    const loginWithGoogle = useMemo(() => {
        if (!notEmpty(turnkeyHandleGoogleOauth))
            return undefined;
        return async () => {
            try {
                await turnkeyHandleGoogleOauth();
            }
            catch (error) {
                console.error("[AuthenticationProvider] loginWithGoogle failed:", error);
                throw new Error(errorMessage(error, "Failed to initiate Google login"));
            }
        };
    }, [turnkeyHandleGoogleOauth]);
    const loginWithApple = useMemo(() => {
        if (!notEmpty(turnkeyHandleAppleOauth))
            return undefined;
        return async () => {
            try {
                await turnkeyHandleAppleOauth();
            }
            catch (error) {
                console.error("[AuthenticationProvider] loginWithApple failed:", error);
                throw new Error(errorMessage(error, "Failed to initiate Apple login"));
            }
        };
    }, [turnkeyHandleAppleOauth]);
    const importWallet = useCallback((mnemonic) => mnemonicImportWallet(mnemonic), [mnemonicImportWallet]);
    // ---------------------------------------------------------------------------
    // Context value
    // ---------------------------------------------------------------------------
    const value = useMemo(() => ({
        authentication,
        isAuthenticated,
        isLoading,
        accounts,
        logout,
        signTransaction,
        signTypedData,
        signUserOperation,
        sendOtpCode,
        completeEmailOtp,
        sendSmsOtpCode,
        completeSmsOtp,
        loginWithGoogle,
        loginWithApple,
        importWallet,
    }), [
        authentication,
        isAuthenticated,
        isLoading,
        accounts,
        logout,
        signTransaction,
        signTypedData,
        signUserOperation,
        sendOtpCode,
        completeEmailOtp,
        sendSmsOtpCode,
        completeSmsOtp,
        loginWithGoogle,
        loginWithApple,
        importWallet,
    ]);
    return _jsx(AuthenticationContext.Provider, { value: value, children: children });
};
